import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  ArrowRight,
  Sparkles,
  ShieldCheck,
  Cpu,
  Quote,
  Mic,
  Award,
  Layers,
} from "lucide-react";
import {
  profile,
  heroStats,
  pillars,
  authorityLogos,
  awards as awardsMock,
  speakingSessions as speakMock,
  caseStudies as caseStudiesMock,
  insights as insightsMock,
  testimonials,
  luminarc,
  images,
} from "../mock/mock";
import RotatingWord from "../components/experience/RotatingWord";
import Counter from "../components/experience/Counter";
import { useCollection } from "../hooks/useCollection";

const pillarIcons = [Sparkles, Cpu, ShieldCheck];

export default function Home() {
  const { data: awards } = useCollection("awards", awardsMock);
  const { data: speakingSessions } = useCollection("speaking", speakMock);
  const { data: caseStudies } = useCollection("caseStudies", caseStudiesMock);
  const { data: insights } = useCollection("insights", insightsMock);
  return (
    <main className="bg-[var(--cream)] text-[var(--ink)]">
      {/* HERO — true 50/50 split */}
      <section className="relative grain border-b border-[var(--ink)]/20">
        <div className="grid grid-cols-1 md:grid-cols-2 min-h-[88vh]">
          {/* LEFT — full-bleed portrait */}
          <div className="relative bg-[var(--ink)] order-2 md:order-1 min-h-[70vh] md:min-h-0 overflow-hidden">
            <img
              src={profile.avatar}
              alt={profile.name}
              className="absolute inset-0 w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-[1200ms]"
            />
            {/* Soft vignette for text legibility */}
            <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-[var(--ink)]/85 to-transparent pointer-events-none" />
            <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-[var(--ink)]/55 to-transparent pointer-events-none" />

            {/* status pill top-left */}
            <div className="absolute top-6 left-6 inline-flex items-center gap-2 bg-[var(--cream)]/95 backdrop-blur px-3 py-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-[var(--molten)] animate-pulse" />
              <span className="font-mono text-[0.62rem] uppercase tracking-[0.2em] text-[var(--ink)]">
                Available · 2026
              </span>
            </div>

            {/* index marker top-right */}
            <div className="absolute top-6 right-6 font-mono text-[0.62rem] uppercase tracking-[0.2em] text-[var(--cream)]/80">
              N° 001
            </div>

            {/* byline bottom */}
            <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between gap-6">
              <div>
                <div className="text-[var(--cream)]" style={{ fontFamily: "Inter", fontWeight: 700, fontSize: "1.6rem", letterSpacing: "-0.035em", lineHeight: 1 }}>
                  Dr. Drijesh Ppatel
                </div>
                <div className="font-mono text-[0.62rem] uppercase tracking-[0.2em] text-[var(--cream)]/70 mt-2">
                  Engineering Manager · IBM · 15+ yrs
                </div>
              </div>
              <div className="font-mono text-[0.62rem] uppercase tracking-[0.2em] text-[var(--cream)]/70 text-right hidden sm:block">
                Gujarat, IN
                <br />
                Remote worldwide
              </div>
            </div>
          </div>

          {/* RIGHT — content, vertically structured */}
          <div className="order-1 md:order-2 flex flex-col justify-between px-6 md:px-12 lg:px-16 py-14 md:py-16">
            {/* top meta */}
            <div className="flex items-center justify-between gap-3 tag-mono flex-wrap">
              <div className="flex items-center gap-3">
                <span className="w-2 h-2 rounded-full bg-[var(--molten)]" />
                Portfolio / 2026
              </div>
              <div>Engineering · GenAI · Cyber</div>
            </div>

            {/* headline + bio */}
            <div className="py-10 md:py-14">
              <h1
                style={{
                  fontFamily: "Inter",
                  fontWeight: 800,
                  fontSize: "clamp(2.6rem, 5.4vw, 5.2rem)",
                  lineHeight: 0.96,
                  letterSpacing: "-0.045em",
                }}
              >
                Building{" "}
                <RotatingWord
                  words={["AI-driven", "secure", "resilient", "intelligent", "scalable"]}
                  interval={2400}
                />{" "}
                systems
                <br />
                &amp; engineering{" "}
                <span className="italic-serif text-[var(--molten)]">excellence.</span>
              </h1>

              <p
                className="mt-8 md:mt-10 max-w-xl leading-[1.35] text-[var(--ink)]"
                style={{ fontSize: "clamp(1rem, 1.35vw, 1.25rem)", fontWeight: 500, letterSpacing: "-0.01em" }}
              >
                Engineering leader helping organisations adopt AI responsibly, build resilient systems, and ship products that move{" "}
                <span className="italic-serif text-[var(--molten)]">real business outcomes.</span>
              </p>

              <p className="mt-5 max-w-xl text-[var(--ink)]/65 leading-relaxed text-[0.95rem]">
                Across cloud, cybersecurity, and modern software — from SDLC transformation to DevSecOps to production-grade GenAI.
              </p>
            </div>

            {/* CTAs */}
            <div className="flex flex-wrap items-center gap-3">
              <Link to="/contact" className="btn-ink">
                <span>Work with me</span> <ArrowUpRight size={14} />
              </Link>
              <Link to="/contact" className="btn-outline">
                <span>Invite me to speak</span> <Mic size={13} />
              </Link>
              <span className="ml-auto tag-mono hidden lg:inline">
                ↓ Scroll for more
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* STATS STRIP — full width separator */}
      <section className="grain">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-2 md:grid-cols-4 border-b border-[var(--ink)]/20">
            {heroStats.map((s, i) => (
              <div
                key={i}
                className={`py-8 md:py-10 px-4 md:px-6 ${
                  i !== heroStats.length - 1 ? "md:border-r border-[var(--ink)]/20" : ""
                } ${i < 2 ? "border-b md:border-b-0 border-[var(--ink)]/20" : ""}`}
              >
                <div className="font-serif text-4xl md:text-6xl" style={{ letterSpacing: "-0.04em" }}>
                  <Counter value={s.value} />
                </div>
                <div className="mt-2 tag-mono">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AUTHORITY MARQUEE */}
      <section className="section-ink grain py-8 overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 mb-4 flex items-center justify-between">
          <span className="tag-mono">Spoken at · Recognized by</span>
          <span className="tag-mono">Leadership · Community · AI</span>
        </div>
        <div className="relative overflow-hidden">
          <div className="marquee-track">
            {[...authorityLogos, ...authorityLogos].map((l, i) => (
              <div
                key={i}
                className="font-serif text-3xl md:text-5xl text-[var(--cream)]/80 whitespace-nowrap flex items-center gap-10"
              >
                {l}
                <span className="text-[var(--molten)]">✦</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT I BRING */}
      <section className="py-24 md:py-36 grain">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-14 rise">
            <div>
              <div className="tag-mono mb-4">[ What I bring ]</div>
              <h2 className="headline-lg max-w-3xl">
                Not another deck. <span className="italic-serif text-[var(--molten)]">Real systems,</span> real teams, real outcomes.
              </h2>
            </div>
            <Link to="/about" className="btn-outline">
              More about me <ArrowRight size={15} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-t border-[var(--ink)]/15">
            {pillars.map((p, i) => {
              const Icon = pillarIcons[i];
              return (
                <div
                  key={p.number}
                  className={`rise p-8 md:p-10 border-b border-[var(--ink)]/15 ${
                    i !== pillars.length - 1 ? "md:border-r" : ""
                  } hover:bg-[var(--cream-soft)] transition-colors duration-500 group`}
                >
                  <div className="flex items-center justify-between mb-8">
                    <span className="num-chip">{p.number}</span>
                    <Icon
                      size={22}
                      className="text-[var(--ink)]/50 group-hover:text-[var(--molten)] transition-colors"
                    />
                  </div>
                  <h3 className="headline-md max-w-sm">{p.title}</h3>
                  <p className="mt-4 text-[var(--ink)]/70 leading-relaxed">{p.copy}</p>
                  <div className="mt-6 flex flex-wrap gap-2">
                    {p.tags.map((t) => (
                      <span
                        key={t}
                        className="text-xs font-mono uppercase tracking-[0.12em] px-2.5 py-1 rounded-full border border-[var(--ink)]/20"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* AWARDS HIGHLIGHTS */}
      <section className="section-ink grain py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-12 gap-8 items-end mb-14">
            <div className="col-span-12 md:col-span-7">
              <div className="tag-mono mb-4">[ Awards &amp; Recognition ]</div>
              <h2 className="headline-lg text-[var(--cream)]">
                A track record of impact,
                <br />
                <span className="italic-serif text-[var(--molten)]">recognized globally.</span>
              </h2>
            </div>
            <div className="col-span-12 md:col-span-5 md:text-right">
              <Link to="/awards" className="btn-cream">
                View full achievements <ArrowUpRight size={16} />
              </Link>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border-t border-[var(--cream)]/20">
            {awards.slice(0, 4).map((a, i) => (
              <a
                key={i}
                href={a.link}
                target="_blank"
                rel="noreferrer"
                className={`rise group flex gap-6 p-7 md:p-9 border-b border-[var(--cream)]/20 ${
                  i % 2 === 0 ? "md:border-r" : ""
                } hover:bg-white/5 transition-colors`}
              >
                <div className="shrink-0 pt-1">
                  <Award size={22} className="text-[var(--molten)]" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between gap-4">
                    <span className="tag-mono">{a.year}</span>
                    <ArrowUpRight
                      size={18}
                      className="text-[var(--cream)]/50 group-hover:text-[var(--molten)] group-hover:translate-x-1 -translate-y-1 transition-all"
                    />
                  </div>
                  <h3 className="headline-md text-[var(--cream)] mt-3">{a.title}</h3>
                  <p className="mt-2 text-[var(--cream)]/65 leading-relaxed max-w-xl">{a.body}</p>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* SPEAKING IMPACT */}
      <section className="py-24 md:py-32 grain">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-12 gap-8">
            <div className="col-span-12 md:col-span-5 rise">
              <div className="tag-mono mb-4">[ Speaking Impact ]</div>
              <h2 className="headline-lg">
                Speaking is not about sharing ideas. It’s about <span className="italic-serif text-[var(--molten)]">enabling action.</span>
              </h2>
              <div className="mt-8 space-y-5 text-[var(--ink)]/75">
                <div className="flex gap-3"><Layers size={18} className="mt-1 text-[var(--molten)]" /><p>Enabled engineers to adopt AI inside real workflows — not demos.</p></div>
                <div className="flex gap-3"><ShieldCheck size={18} className="mt-1 text-[var(--molten)]" /><p>Helped teams rethink DevSecOps and system design from first principles.</p></div>
                <div className="flex gap-3"><Sparkles size={18} className="mt-1 text-[var(--molten)]" /><p>Delivered practical frameworks, not abstract concepts.</p></div>
              </div>
              <div className="mt-10 flex gap-3">
                <Link to="/contact" className="btn-ink">Invite me to speak <Mic size={15} /></Link>
                <Link to="/insights" className="btn-outline">Read insights <ArrowRight size={15} /></Link>
              </div>
            </div>

            <div className="col-span-12 md:col-span-7">
              <div className="grid grid-cols-2 gap-4 mb-6">
                <img src={images.speakingHero} alt="Keynote" className="w-full h-60 md:h-72 object-cover grayscale hover:grayscale-0 transition-all duration-700" />
                <img src={images.speakingSecondary} alt="Conference" className="w-full h-60 md:h-72 object-cover grayscale hover:grayscale-0 transition-all duration-700 mt-8" />
              </div>
              <div className="border-t border-[var(--ink)]/15">
                {speakingSessions.slice(0, 4).map((s, i) => (
                  <a
                    key={i}
                    href={s.url}
                    target="_blank"
                    rel="noreferrer"
                    className="rise group flex items-center justify-between gap-4 py-5 border-b border-[var(--ink)]/15 hover:bg-[var(--cream-soft)] px-2 -mx-2 transition-colors"
                  >
                    <div>
                      <div className="tag-mono mb-1">{s.venue}</div>
                      <div className="font-serif text-xl md:text-2xl">{s.title}</div>
                      <div className="text-sm text-[var(--ink)]/60 mt-1">{s.topic}</div>
                    </div>
                    <ArrowUpRight size={20} className="text-[var(--ink)]/40 group-hover:text-[var(--molten)] group-hover:-translate-y-1 group-hover:translate-x-1 transition-all shrink-0" />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* LUMINARC */}
      <section className="py-24 md:py-32 grain bg-[var(--cream-soft)]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-12 gap-8 items-stretch">
            <div className="col-span-12 md:col-span-5 rise">
              <img src={images.workshopLab} alt="Luminarc" className="w-full h-80 md:h-full object-cover grayscale" />
            </div>
            <div className="col-span-12 md:col-span-7 rise">
              <div className="tag-mono mb-4">[ Luminarc™ · Flagship experience ]</div>
              <h2 className="headline-lg max-w-2xl">
                <span>{luminarc.trademark}</span> —{" "}
                <span className="italic-serif text-[var(--molten)]">{luminarc.tagline}</span>
              </h2>
              <p className="mt-6 text-[var(--ink)]/75 max-w-2xl leading-relaxed">
                {luminarc.hero}
              </p>
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                {luminarc.principles.slice(0, 4).map((p) => (
                  <div key={p.t} className="flex items-start gap-3">
                    <span className="mt-2 w-1.5 h-1.5 bg-[var(--molten)] shrink-0" />
                    <span className="text-[var(--ink)]/80">{p.t}</span>
                  </div>
                ))}
              </div>
              <div className="mt-10 flex gap-3 flex-wrap">
                <Link to="/luminarc" className="btn-ink">Explore Luminarc™ <ArrowUpRight size={14} /></Link>
                <a href={luminarc.ctaUrl} target="_blank" rel="noreferrer" className="btn-outline">{luminarc.ctaLabel} <ArrowRight size={13} /></a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CASE STUDIES */}
      <section className="py-24 md:py-32 grain">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="flex flex-wrap justify-between items-end gap-6 mb-14 rise">
            <div>
              <div className="tag-mono mb-4">[ Case Studies ]</div>
              <h2 className="headline-lg">
                Real systems. <span className="italic-serif text-[var(--molten)]">Real impact.</span>
              </h2>
            </div>
            <Link to="/contact" className="btn-outline">Request a deep-dive <ArrowRight size={15} /></Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {caseStudies.map((c, i) => (
              <div key={i} className="rise p-8 border border-[var(--ink)]/15 rounded-sm hover:border-[var(--molten)] transition-colors group">
                <div className="tag-mono mb-6">{c.tag}</div>
                <div className="font-serif italic text-3xl text-[var(--molten)] mb-5">{c.metric}</div>
                <h3 className="headline-md mb-3">{c.title}</h3>
                <p className="text-[var(--ink)]/70 leading-relaxed">{c.copy}</p>
                <div className="mt-7 flex items-center gap-2 text-sm font-mono uppercase tracking-[0.12em] text-[var(--ink)]/60 group-hover:text-[var(--molten)] transition-colors">
                  Case study <ArrowUpRight size={14} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIAL */}
      <section className="py-20 md:py-28 grain">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <blockquote key={i} className="rise p-8 border-t-2 border-[var(--ink)]">
                <Quote size={22} className="text-[var(--molten)] mb-4" />
                <p className="font-serif text-xl md:text-2xl leading-snug text-[var(--ink)]">
                  {t.quote}
                </p>
              </blockquote>
            ))}
          </div>
        </div>
      </section>

      {/* INSIGHTS TEASER */}
      <section className="py-24 md:py-32 grain bg-[var(--cream-soft)]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-12 rise">
            <div>
              <div className="tag-mono mb-4">[ Thought Leadership ]</div>
              <h2 className="headline-lg max-w-2xl">
                Practical insights on AI, leadership, and systems that <span className="italic-serif text-[var(--molten)]">scale.</span>
              </h2>
            </div>
            <Link to="/insights" className="btn-outline">All writing <ArrowRight size={15} /></Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border-t border-[var(--ink)]/15">
            {insights.map((p, i) => (
              <div
                key={i}
                className={`rise p-7 md:p-9 border-b border-[var(--ink)]/15 ${
                  i % 2 === 0 ? "md:border-r" : ""
                } hover:bg-white transition-colors group cursor-pointer`}
              >
                <div className="flex items-center justify-between mb-5">
                  <span className="tag-mono">{p.tag}</span>
                  <span className="tag-mono">{p.date} · {p.read}</span>
                </div>
                <h3 className="headline-md group-hover:text-[var(--molten)] transition-colors">{p.title}</h3>
                <p className="mt-3 text-[var(--ink)]/70 leading-relaxed max-w-xl">{p.excerpt}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
